import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import axiosInstance from "../axiosinstance";
import API_URL from "../Config";
import "./CreateFlangePage.css";

const getProjectIdValue = (project) => {
  if (!project) return "";
  if (typeof project === "object") {
    return String(project?.id || project?.project_id || "").trim();
  }
  return String(project).trim();
};

const createInitialForm = (projectId = "") => ({
  project_id: projectId,
  equipment_number: "",
  remark: "",
  equipment_name: "",
  flange_type: "",
  flange_size: "",
  rating: "",
  stud_size: "",
  stud_length: "",
  number_of_studs: "",
  bolt_diameter: "",
  stud_nut_moc: "",
  spanner_size: "",
  gasket_moc: "",
  lubricant_used: "",
  required_torque: "",
  method_of_tightening: "",
  torquing_type: "",
});

const validateForm = (form) => {
  const errors = {};
  const flangeType = (form.flange_type || "").trim();
  const equipmentNo = (form.equipment_number || "").trim();
  const remark = (form.remark || "").trim();

  if (!(form.project_id || "").trim()) {
    errors.project_id = "Project ID is required.";
  }
  if (!["RF", "RTJ"].includes(flangeType)) {
    errors.flange_type = "Flange type must be RF or RTJ.";
  }
  if (!equipmentNo && !remark) {
    errors.remark = "Remark is required when equipment number is empty.";
  }

  return errors;
};

function CreateFlangePage({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject")) || [];
  const projectIdFromState = getProjectIdValue(location?.state?.project_id || location?.state?.projectId);
  const selectedProjectId = getProjectIdValue(selectedProject);
  const resolvedProjectId = projectIdFromState || selectedProjectId;
  const [form, setForm] = useState(() =>
    createInitialForm(resolvedProjectId)
  );
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [equipmentNameOptions, setEquipmentNameOptions] = useState([]);
  const [equipmentNameLoading, setEquipmentNameLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [projectInfo, setProjectInfo] = useState(null);
  const [projectInfoLoading, setProjectInfoLoading] = useState(false);
  const [projectInfoError, setProjectInfoError] = useState("");

  const formatDateDisplay = (value) => {
    if (!value) return "N/A";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const setField = (field, value) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      setErrors(validateForm(next));
      return next;
    });
  };

  useEffect(() => {
    // Keep project_id internal and synced with selected/current project context.
    setForm((prev) => ({
      ...prev,
      project_id: resolvedProjectId,
    }));
  }, [resolvedProjectId]);

  useEffect(() => {
    const fetchProjectInfo = async () => {
      if (!resolvedProjectId) {
        setProjectInfo(null);
        setProjectInfoError("No project selected.");
        return;
      }

      try {
        setProjectInfoLoading(true);
        setProjectInfoError("");
        const res = await axiosInstance.get(
          `${API_URL}/api/get_project/${resolvedProjectId}`
        );
        setProjectInfo(res?.data?.data || null);
      } catch (error) {
        console.error(
          "Failed to fetch project information:",
          error?.response?.data || error?.message
        );
        setProjectInfo(null);
        setProjectInfoError("Unable to load project information.");
      } finally {
        setProjectInfoLoading(false);
      }
    };

    fetchProjectInfo();
  }, [resolvedProjectId]);

  useEffect(() => {
    const fetchEquipmentNames = async () => {
      const projectId = (form.project_id || "").trim();
      if (!projectId) {
        setEquipmentNameOptions([]);
        return;
      }
      try {
        setEquipmentNameLoading(true);
        const res = await axiosInstance.get(`${API_URL}/get_equipment_group`, {
          params: { project_selected: projectId, search: "" },
        });
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        const names = rows
          .map((row) => (typeof row === "string" ? row : row?.group_name))
          .filter(Boolean);
        setEquipmentNameOptions(Array.from(new Set(names)));
      } catch (error) {
        console.error(
          "Failed to fetch equipment names:",
          error?.response?.data || error?.message
        );
        setEquipmentNameOptions([]);
      } finally {
        setEquipmentNameLoading(false);
      }
    };

    fetchEquipmentNames();
  }, [form.project_id]);

  const canSubmit = useMemo(() => {
    return !submitting && Object.keys(validateForm(form)).length === 0;
  }, [form, submitting]);

  const showFieldError = (field, condition = true) => {
    if (!condition) return false;
    return Boolean((attemptedSubmit || touched[field]) && errors[field]);
  };

  const renderFieldError = (field, condition = true) =>
    showFieldError(field, condition) ? (
      <div className="invalid-feedback d-block">{errors[field]}</div>
    ) : null;

  const projectSummaryItems = useMemo(() => {
    if (!projectInfo) return [];

    const candidates = [
      { label: "Project Name", value: projectInfo.name },
      { label: "Project Code", value: projectInfo.project_code },
      { label: "Work Order ID", value: projectInfo.work_order_id },
      { label: "Client Name", value: projectInfo.client_name },
      { label: "Start Date", value: formatDateDisplay(projectInfo.start_date) },
      { label: "End Date", value: formatDateDisplay(projectInfo.end_date) },
      { label: "Location", value: projectInfo.location },
      { label: "Timezone", value: projectInfo.timezone },
      
      { label: "Status", value: projectInfo.status || projectInfo.project_status },
    ];

    return candidates.filter((item) => item.value !== undefined && item.value !== null && String(item.value).trim() !== "");
  }, [projectInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    setAttemptedSubmit(true);

    if (Object.keys(nextErrors).length > 0) return;

    const parsedStuds = Number(form.number_of_studs);
    const payload = {
      project_id: (form.project_id || "").trim(),
      equipment_number: (form.equipment_number || "").trim(),
      remark: (form.remark || "").trim(),
      equipment_name: (form.equipment_name || "").trim(),
      flange_type: (form.flange_type || "").trim(),
      flange_size: (form.flange_size || "").trim(),
      rating: (form.rating || "").trim(),
      stud_size: (form.stud_size || "").trim(),
      stud_length: (form.stud_length || "").trim(),
      number_of_studs:
        (form.number_of_studs || "").toString().trim() === ""
          ? null
          : Number.isFinite(parsedStuds)
          ? parsedStuds
          : null,
      bolt_diameter: (form.bolt_diameter || "").trim(),
      stud_nut_moc: (form.stud_nut_moc || "").trim(),
      spanner_size: (form.spanner_size || "").trim(),
      gasket_moc: (form.gasket_moc || "").trim(),
      lubricant_used: (form.lubricant_used || "").trim(),
      required_torque: (form.required_torque || "").trim(),
      method_of_tightening: (form.method_of_tightening || "").trim(),
      torquing_type: (form.torquing_type || "").trim(),
    };

    try {
      setSubmitting(true);
      const response = await axiosInstance.post("/api/create_flange", payload);
      setSnackbarMessage(response?.data?.message || "Flange created successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      window.dispatchEvent(new Event("flangeUpdated"));
      setTimeout(() => navigate("/flanges"), 300);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to create flange";
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`table-container ${isSidebarExpanded ? "expanded" : "collapsed"}`}
      style={{
        padding: "unset !important",
        minHeight: "unset !important",
        borderRadius: "0px",
      }}
    >
      <div className="table-container-content">
        <div className="">
          <div className="form-body p-4">
            <h4 className="mb-1 fw-bold">Create New Flange</h4>
            <p className="text-muted mb-2">Add the flange details below</p>

            <div className="create-flange-project-card mb-3">
              <div className="create-flange-project-card-header">
                <h5 className="mb-0">Project Information</h5>
              </div>

              {projectInfoLoading && (
                <div className="create-flange-project-loading">Loading project information...</div>
              )}

              {!projectInfoLoading && projectInfoError && (
                <div className="create-flange-project-error">{projectInfoError}</div>
              )}

              {!projectInfoLoading && !projectInfoError && projectSummaryItems.length > 0 && (
                <div className="create-flange-project-grid">
                  {projectSummaryItems.map((item) => (
                    <div key={item.label} className="create-flange-project-item">
                      <p className="create-flange-project-label mb-1">{item.label}</p>
                      <p className="create-flange-project-value mb-0">{item.value}</p>
                    </div>
                  ))}
                </div>
              )}

              {!projectInfoLoading && !projectInfoError && projectSummaryItems.length === 0 && (
                <div className="create-flange-project-loading">No project metadata available.</div>
              )}
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="equipment_number" className="form-label text-capitalize fw-semibold">
                    Equipment Number
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="equipment_number"
                    name="equipment_number"
                    value={form.equipment_number || ""}
                    onChange={(e) => setField("equipment_number", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="equipment_name" className="form-label text-capitalize fw-semibold">
                    Equipment Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="equipment_name"
                    name="equipment_name"
                    list="equipment-name-options"
                    value={form.equipment_name || ""}
                    onChange={(e) => setField("equipment_name", e.target.value)}
                  />
                  <datalist id="equipment-name-options">
                    {equipmentNameOptions.map((opt) => (
                      <option key={opt} value={opt} />
                    ))}
                  </datalist>
                  {equipmentNameLoading && (
                    <div className="form-text text-muted">Loading equipment names...</div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="flange_type" className="form-label text-capitalize fw-semibold">
                    Flange Type <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${showFieldError("flange_type") ? "is-invalid" : ""}`}
                    id="flange_type"
                    name="flange_type"
                    value={form.flange_type || ""}
                    onChange={(e) => setField("flange_type", e.target.value)}
                  >
                    <option value="">Select flange type</option>
                    <option value="RF">RF</option>
                    <option value="RTJ">RTJ</option>
                  </select>
                  {renderFieldError("flange_type")}
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="flange_size" className="form-label text-capitalize fw-semibold">
                    Flange Size
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="flange_size"
                    name="flange_size"
                    value={form.flange_size || ""}
                    onChange={(e) => setField("flange_size", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="rating" className="form-label text-capitalize fw-semibold">
                    Rating
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="rating"
                    name="rating"
                    value={form.rating || ""}
                    onChange={(e) => setField("rating", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="stud_size" className="form-label text-capitalize fw-semibold">
                    Stud Size
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="stud_size"
                    name="stud_size"
                    value={form.stud_size || ""}
                    onChange={(e) => setField("stud_size", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="stud_length" className="form-label text-capitalize fw-semibold">
                    Stud Length
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="stud_length"
                    name="stud_length"
                    value={form.stud_length || ""}
                    onChange={(e) => setField("stud_length", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="number_of_studs" className="form-label text-capitalize fw-semibold">
                    Number of Studs
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    id="number_of_studs"
                    name="number_of_studs"
                    value={form.number_of_studs || ""}
                    onChange={(e) => setField("number_of_studs", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="bolt_diameter" className="form-label text-capitalize fw-semibold">
                    Bolt Diameter
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="bolt_diameter"
                    name="bolt_diameter"
                    value={form.bolt_diameter || ""}
                    onChange={(e) => setField("bolt_diameter", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="stud_nut_moc" className="form-label text-capitalize fw-semibold">
                    Stud Nut MOC
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="stud_nut_moc"
                    name="stud_nut_moc"
                    value={form.stud_nut_moc || ""}
                    onChange={(e) => setField("stud_nut_moc", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="spanner_size" className="form-label text-capitalize fw-semibold">
                    Spanner Size
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="spanner_size"
                    name="spanner_size"
                    value={form.spanner_size || ""}
                    onChange={(e) => setField("spanner_size", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="gasket_moc" className="form-label text-capitalize fw-semibold">
                    Gasket MOC
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="gasket_moc"
                    name="gasket_moc"
                    value={form.gasket_moc || ""}
                    onChange={(e) => setField("gasket_moc", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="lubricant_used" className="form-label text-capitalize fw-semibold">
                    Lubricant Used
                  </label>
                  <select
                    className="form-select"
                    id="lubricant_used"
                    name="lubricant_used"
                    value={form.lubricant_used || ""}
                    onChange={(e) => setField("lubricant_used", e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="required_torque" className="form-label text-capitalize fw-semibold">
                    Required Torque
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="required_torque"
                    name="required_torque"
                    value={form.required_torque || ""}
                    onChange={(e) => setField("required_torque", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="method_of_tightening" className="form-label text-capitalize fw-semibold">
                    Method of Tightening
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="method_of_tightening"
                    name="method_of_tightening"
                    value={form.method_of_tightening || ""}
                    onChange={(e) => setField("method_of_tightening", e.target.value)}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor="torquing_type" className="form-label text-capitalize fw-semibold">
                    Torquing Type
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="torquing_type"
                    name="torquing_type"
                    value={form.torquing_type || ""}
                    onChange={(e) => setField("torquing_type", e.target.value)}
                  />
                </div>

                <div className="col-12 mb-3">
                  <label htmlFor="remark" className="form-label text-capitalize fw-semibold">
                    Remark
                    {!(form.equipment_number || "").trim() && (
                      <span className="text-danger"> *</span>
                    )}
                  </label>
                  <textarea
                    className={`form-control ${
                      showFieldError("remark", !form.equipment_number?.trim())
                        ? "is-invalid"
                        : ""
                    }`}
                    id="remark"
                    name="remark"
                    rows={3}
                    value={form.remark || ""}
                    onChange={(e) => setField("remark", e.target.value)}
                  />
                  {renderFieldError(
                    "remark",
                    !form.equipment_number?.trim() &&
                      (attemptedSubmit || touched.remark || touched.equipment_number)
                  )}
                </div>
              </div>

              <div className="d-flex justify-content-end mt-4 gap-2">
                <button
                  type="button"
                  className="btn btn-light border"
                  onClick={() => navigate("/flanges")}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-3"
                  disabled={!canSubmit}
                >
                  {submitting ? "Saving..." : "Save"}
                </button>
              </div>
              {showFieldError("project_id") && (
                <div className="invalid-feedback d-block mt-2">{errors.project_id}</div>
              )}
            </form>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default CreateFlangePage;
